/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useEffect, useMemo } from 'react';

import { useIntl, defineMessages } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { IconButton, LinkButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import { ConfluenceLogo, JiraLogo, LoomLogo } from '@atlaskit/logo';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import {
	type RecommendedApp,
	type ActionTier,
} from '@post-office/message-template--twc-sidenav-banner';

const styles = cssMap({
	root: {
		backgroundColor: token('color.background.accent.blue.subtlest'),
		borderRadius: token('radius.large'),
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		marginTop: token('space.150'),
		marginRight: token('space.150'),
		marginBottom: token('space.150'),
		marginLeft: token('space.150'),
	},
	headerRow: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		gap: token('space.150'),
	},
	logoWrapper: {
		marginTop: token('space.075'),
	},
});

const messages = defineMessages({
	subheadingConfluence: {
		id: 'twc.sidenav.banner.personalized.subheading.confluence',
		defaultMessage: 'Create with Rovo',
		description:
			'Personalized banner subheading when the recommended app is Confluence. Shown in the product sidenav.',
	},
	subheadingJira: {
		id: 'twc.sidenav.banner.personalized.subheading.jira',
		defaultMessage: 'Plan your next team project',
		description:
			'Personalized banner subheading when the recommended app is Jira. Shown in the product sidenav.',
	},
	subheadingLoom: {
		id: 'twc.sidenav.banner.personalized.subheading.loom',
		defaultMessage: 'Set up video meetings',
		description:
			'Personalized banner subheading when the recommended app is Loom. Shown in the product sidenav.',
	},
	logoLabelConfluence: {
		id: 'twc.sidenav.banner.personalized.logo.label.confluence',
		defaultMessage: 'Confluence',
		description: 'Accessible label for the Confluence product logo lockup. Read by screen readers.',
	},
	logoLabelJira: {
		id: 'twc.sidenav.banner.personalized.logo.label.jira',
		defaultMessage: 'Jira',
		description: 'Accessible label for the Jira product logo lockup. Read by screen readers.',
	},
	logoLabelLoom: {
		id: 'twc.sidenav.banner.personalized.logo.label.loom',
		defaultMessage: 'Loom',
		description: 'Accessible label for the Loom product logo lockup. Read by screen readers.',
	},
	heading: {
		id: 'twc.sidenav.banner.personalized.heading',
		defaultMessage: 'Explore Teamwork Collection',
		description:
			'Main heading for the personalized missing-app activation banner in the product sidenav.',
	},
	ctaButton: {
		id: 'twc.sidenav.banner.personalized.cta',
		defaultMessage: 'Get started',
		description:
			'CTA button text for the personalized missing-app activation banner in the product sidenav.',
	},
	closeLabel: {
		id: 'twc.sidenav.banner.personalized.close',
		defaultMessage: 'Close',
		description:
			'Accessible label for the close button on the personalized missing-app activation banner.',
	},
});

const subheadingByApp: Record<RecommendedApp, (typeof messages)[keyof typeof messages]> = {
	confluence: messages.subheadingConfluence,
	jira: messages.subheadingJira,
	loom: messages.subheadingLoom,
};

const logoLabelByApp: Record<RecommendedApp, (typeof messages)[keyof typeof messages]> = {
	confluence: messages.logoLabelConfluence,
	jira: messages.logoLabelJira,
	loom: messages.logoLabelLoom,
};

const logoComponentByApp = {
	confluence: ConfluenceLogo,
	jira: JiraLogo,
	loom: LoomLogo,
} as const;

export type PersonalizedBannerProps = {
	href: string;
	locale?: string;
	recommendedApp: RecommendedApp;
	messageInstanceId?: string;
	onClose?: () => void;
	onCtaClick?: () => void;
	experimentKey?: string;
	actionTier?: ActionTier;
	targetTenantId?: string;
	collectionEntitlementId?: string;
};

export const PersonalizedBanner = ({
	href,
	locale,
	recommendedApp,
	messageInstanceId,
	onClose,
	onCtaClick,
	experimentKey,
	actionTier,
	targetTenantId,
	collectionEntitlementId,
}: PersonalizedBannerProps): JSX.Element => {
	const { formatMessage } = useIntl();

	const logoLabel = formatMessage(logoLabelByApp[recommendedApp]);
	const LogoComponent = logoComponentByApp[recommendedApp];
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const analyticsAttributes = useMemo(
		() => ({
			recommendedApp,
			messageInstanceId,
			locale,
			experimentKey,
			actionTier,
			targetTenantId,
			collectionEntitlementId,
			arm: 'personalized' as const,
		}),
		[
			recommendedApp,
			messageInstanceId,
			locale,
			experimentKey,
			actionTier,
			targetTenantId,
			collectionEntitlementId,
		],
	);

	useEffect(() => {
		createAnalyticsEvent({
			eventType: 'screen',
			name: 'twcPersonalizedBanner',
			attributes: analyticsAttributes,
		}).fire('growth');
	}, [analyticsAttributes, createAnalyticsEvent]);

	const handleClose = () => {
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'twcPersonalizedBannerClose',
			attributes: analyticsAttributes,
		}).fire('growth');
		onClose?.();
	};

	const handleCtaClick = () => {
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'twcPersonalizedBannerCta',
			attributes: analyticsAttributes,
		}).fire('growth');
		onCtaClick?.();
	};

	return (
		<Box xcss={styles.root} testId="twc-personalized-banner">
			<Stack space="space.150">
				<Box xcss={styles.headerRow}>
					<Box xcss={styles.logoWrapper} testId="twc-personalized-banner-logo">
						<LogoComponent
							shouldUseNewLogoDesign
							appearance="brand"
							size="xsmall"
							label={logoLabel}
						/>
					</Box>
					<IconButton
						icon={(iconProps) => <CrossIcon {...iconProps} size="small" />}
						onClick={handleClose}
						label={formatMessage(messages.closeLabel)}
						testId="twc-personalized-banner-close"
						appearance="subtle"
					/>
				</Box>
				<Stack space="space.100">
					<Heading size="xsmall" testId="twc-personalized-banner-heading">
						{formatMessage(messages.heading)}
					</Heading>
					<Text size="small" color="color.text.subtle" testId="twc-personalized-banner-subheading">
						{formatMessage(subheadingByApp[recommendedApp])}
					</Text>
				</Stack>
				<LinkButton
					shouldFitContainer
					href={href}
					testId="twc-personalized-banner-cta"
					onClick={handleCtaClick}
					target="_blank"
				>
					{formatMessage(messages.ctaButton)}
				</LinkButton>
			</Stack>
		</Box>
	);
};
